import { html } from "lit";
import { connect } from "pwa-helpers";
import { abandonedCartPreference } from "./abandoned-cart-preference.thunks";
import { AbandonedCartPreferenceActions, AbandonedCartPreferenceStatus } from "./abandoned-cart-preference.interface";
import { selectAbandonedCartPreference, isAbandonedCartPreferenceLoaded } from "./abandoned-cart-preference.selectors";
import { getQueryParam } from "../../common/utils/util";
import { BaseComponent } from "../../features/base-component/base-component";
import { booleanConverter } from "../../common/converters";
import { StoreState } from "../../store/store.interface";
import { loading, smallSpinner } from "../../common/constants/api-constants";
import store from "../../store/store";
import emit from "../../common/events";
import { customElement, property, state } from "lit/decorators.js";

/**
 * The various statuses that the abandoned cart preference component can have.
 */
enum Status {
    subscribed = 'subscribed',
    unsubscribed = 'unsubscribed',
    loading = 'loading',
    unsubscribing = 'unsubscribing',
    alreadyUnsubscribed = 'alreadyUnsubscribed',
    error = 'error',
    noToken = 'noToken',
}

/**
 * The GET parameter to look for in order to find the token that
 * authenticates the abandoned cart email preference apis.
 */
const TOKEN_QUERY_PARAM = 'unsubscribeId';

/**
 * A component that allows the customer to unsubscribe from cart reminder emails.
 *
 * @event click - A user has clicked the unsubscribe button.
 *     <br><br>&cross; dataLayer
 *     <br><span style="color: green">&check;</span> bubbles
 *     <br><br>event.detail: { action, eventName }
 */
@customElement('mhecomm-abandoned-cart-preference')
export class AbandonedCartPreference extends connect(store)(BaseComponent) {
    /**
     * A title to display to the customer.
     */
    @property()
    title = 'Unsubscribe from cart reminders';

    /**
     * A message to display to the customer to explain to them how they can unsubscribe.
     */
    @property()
    message = 'To opt-out of cart reminders please click the unsubscribe button below. It can take up to 48 hours for your remail to be completely removed from these emails.';

    /**
     * A message to display to the customer after they have successfully unsubscribed.
     */
    @property()
    unsubscribedMessage = "You've been unsubscribed.";

    /**
     * A message to display to the customer after they have unsubscribed.
     */
    @property()
    alreadyUnsubscribedMessage = "You've already unsubscribed.";

    /**
     * A message to display to the customer if no token is found in the url. This can happen if someone goes directly
     * to the page without coming from the cart reminder email.
     */
    @property()
    noTokenMessage = "In order to unsubscribe from cart reminders, please follow the link from the cart reminder email.";

    /**
     * The button label on the button that initiates the unsubscribe action.
     */
    @property()
    unsubscribeButtonLabel = 'Unsubscribe';

    /**
     * The button label on the button that initiates the unsubscribe action.
     */
    @property({ converter: booleanConverter })
    preview: boolean | undefined;

    /**
     * The current state of the component. This is used to update the UI while the component is waiting on API calls
     * to finish, after user actions but before the API call finishes, and based on the results of the API call.
     */
    @state()
    private status: Status = Status.loading;

    /**
     * The token found in the url that can be used to authenticate the abandoned cart preference apis.
     */
    @state()
    private token = '';

    connectedCallback() {
        super.connectedCallback();
        this.token = getQueryParam(TOKEN_QUERY_PARAM) || '';
        if (this.token) {
            store.dispatch(abandonedCartPreference({ token: this.token, action: AbandonedCartPreferenceActions.get }));
        } else {
            this.status = Status.noToken;
        }
    }

    stateChanged(state: StoreState) {
        if (isAbandonedCartPreferenceLoaded(state)) {
            // Yes this is a one to one mapping. However, the internal state of this component
            // has more options than the possible statuses returned by the api.
            this.status = {
                [AbandonedCartPreferenceStatus.subscribed]: Status.subscribed,
                [AbandonedCartPreferenceStatus.unsubscribed]: Status.unsubscribed,
                [AbandonedCartPreferenceStatus.alreadyUnsubscribed]: Status.alreadyUnsubscribed,
            }[selectAbandonedCartPreference(state)] || Status.error;
        }
    }

    renderComp() {
        if ([Status.subscribed, Status.unsubscribing].includes(this.status) || this.preview) {
            return html`
                <h1>${this.title}</h1>
                <p>${this.message}</p>
                <button @click=${() => this.unsubscribe()} class="btn btn-lg btn-primary" ?disabled=${this.status === Status.unsubscribing}>
                    ${this.status === Status.unsubscribing ? smallSpinner() : this.unsubscribeButtonLabel}
                </button>
            `;
        } else if (this.status === Status.unsubscribed) {
            return html`
                <p>${this.unsubscribedMessage}</p>
            `;
        } else if (this.status === Status.loading) {
            return html`
                ${loading({ columns: 1, m: 'mt-1', width: '60%', height: '3rem' })}
                ${loading({ columns: 1, m: 'mt-3', width: '100%' })}
                ${loading({ columns: 1, m: 'mt-3', width: '30%' })}
            `;
        } else if (this.status === Status.alreadyUnsubscribed) {
            return html`
                <p>${this.alreadyUnsubscribedMessage}</p>
            `;
        } else if (this.status === Status.noToken) {
            return html`
                <p>${this.noTokenMessage}</p>
            `;
        } else {
            return html`
                <p>
                    There was an error unsubscribing from cart reminders.
                    If you still need to unsubscribe, please contact
                    <a href="https://www.mheducation.com/support.html">McGraw Hill Support</a>.
                </p>
            `
        }
    }

    /**
     * Initiate a call to unsubscribe from cart reminders using the token found in the url.
     */
    unsubscribe() {
        this.status = Status.unsubscribing;
        store.dispatch(abandonedCartPreference({ token: this.token, action: AbandonedCartPreferenceActions.unsubscribe }));
        emit({
            element: this,
            name: 'click',
            detail: {
                eventName: 'update-abandoned-cart-preference',
                action: 'unsubscribe'
            }
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'mhecomm-abandoned-cart-preference': AbandonedCartPreference;
    }
}
